import type { ReactNode, Ref } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Button, IconButton } from '@cbhq/cds-web/buttons';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import type { TrayRefProps } from '@cbhq/cds-web/overlays/tray/Tray';
import { Tray } from '@cbhq/cds-web/overlays/tray/Tray';
import { PageFooter } from '@cbhq/cds-web/page/PageFooter';
import { Text } from '@cbhq/cds-web/typography/Text';

const messages = defineMessages({
  title: {
    id: 'Team.inviteTray.title',
    defaultMessage: 'Invite team',
    description: 'Invite tray title',
  },
  closeAccessibilityLabel: {
    id: 'Team.inviteTray.closeAccessibilityLabel',
    defaultMessage: 'Close invite team tray',
    description: 'Accessibility label for the invite tray close button',
  },
  sendInvite: {
    id: 'Team.inviteTray.sendInvite',
    defaultMessage: 'Send invite',
    description: 'Disabled footer button label for the invite tray scaffold',
  },
  legalText: {
    id: 'Team.inviteTray.legalText',
    defaultMessage:
      'By inviting a team member to your workspace, you confirm you are authorized to grant them access on behalf of your organization. Each team member will only be given the access given to the role you assign them and you are wholly responsible for the management of such access, including the removal of access or changing of roles when necessary. Each team member will be fully subject to the terms of your User Agreement and any other terms or agreements applicable to your Coinbase account. Your organization is responsible for all actions taken by invited team members.',
    description: 'Legal disclaimer shown below the send invite button in the invite tray',
  },
});

export type InviteTrayParams = {
  trayRef: Ref<TrayRefProps>;
  onClose: () => void;
  onInvite: () => void;
  beforeClose: () => void;
  isSubmitting: boolean;
  children: ReactNode;
};

const TRAY_STYLES = { container: { width: '532px' } } as const;
const TRAY_TITLE_ID = 'invite-team-tray-title';

export function InviteTray({
  trayRef,
  onClose,
  onInvite,
  beforeClose,
  isSubmitting,
  children,
}: InviteTrayParams) {
  const { formatMessage } = useIntl();
  const title = formatMessage(messages.title);

  return (
    <Tray
      ref={trayRef}
      accessibilityLabel={title}
      accessibilityLabelledBy={TRAY_TITLE_ID}
      hideCloseButton
      onCloseComplete={onClose}
      pin="right"
      preventDismiss
      styles={TRAY_STYLES}
      title={
        <HStack width="100%" alignItems="center" justifyContent="space-between">
          <Text id={TRAY_TITLE_ID} font="title3">
            {title}
          </Text>
          <IconButton
            transparent
            color="fg"
            name="close"
            onClick={beforeClose}
            accessibilityLabel={formatMessage(messages.closeAccessibilityLabel)}
          />
        </HStack>
      }
      footer={
        <PageFooter
          borderedTop
          height="auto"
          action={
            <VStack width="100%" alignItems="flex-end" gap={2}>
              <Button
                variant="primary"
                onClick={onInvite}
                disabled={isSubmitting}
                loading={isSubmitting}
                compact
              >
                {formatMessage(messages.sendInvite)}
              </Button>
              <Text
                as="p"
                font="legal"
                color="fgMuted"
                textAlign="end"
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                alignSelf="stretch"
                maxWidth={500}
              >
                {formatMessage(messages.legalText)}
              </Text>
            </VStack>
          }
        />
      }
    >
      <VStack width="100%" gap={4} paddingTop={4}>
        <VStack width="100%" gap={3}>
          {children}
        </VStack>
      </VStack>
    </Tray>
  );
}
